/**
 * amazonClick — outbound Amazon-affiliate click handler.
 *
 * Mirrors the sync-popup pattern from `lib/bookNow.js`: open the tab
 * synchronously on the user gesture, then redirect it after the API
 * returns. Amazon's `?tag=` is safer than Booking's CJ chain (no
 * cross-domain redirect flash), so we don't strictly need the
 * `/booking-transit` interstitial — but we route through it anyway
 * for consistency and to avoid any brief `about:blank` while the API
 * resolves.
 */
import { toast } from "sonner";
import { api } from "./api";

const FAILURE_TOAST = "Couldn't open the Amazon link right now. Please try again.";

function _openBlank() {
  try { return window.open("about:blank", "_blank"); } catch { return null; }
}
function _navigateBlank(blank, url) {
  if (!blank) { window.location.href = url; return; }
  try { try { blank.opener = null; } catch (_) { /* noop */ }
        blank.location.href = url;
  } catch { window.location.href = url; }
}
function _closeBlank(blank) { try { blank && blank.close(); } catch (_) { /* noop */ } }
function _wrapWithTransit(rawUrl) {
  if (!rawUrl) return rawUrl;
  try {
    return `${window.location.origin}/booking-transit?url=${encodeURIComponent(rawUrl)}`;
  } catch {
    return rawUrl;
  }
}

/**
 * Open an Amazon affiliate URL in a new tab and log the click.
 * Guarantees the outbound URL carries our `tag=travelbio-20`.
 *
 * Usage:
 *   await goAmazon({ url: item.amazon_url, itemId: item.id });
 */
export async function goAmazon({
  url = null,
  itemName = null,
  itemId = null,
  listId = null,
  onLoadingChange = null,
} = {}) {
  if (!url && !itemName) {
    console.warn("[goAmazon] no url or itemName provided");
    return { error: "no_url_or_name" };
  }
  const blank = _openBlank();
  try {
    onLoadingChange && onLoadingChange(true);
    const { data } = await api.post("/amazon-clicks/outbound", {
      ...(url ? { url } : {}),
      ...(itemName ? { item_name: itemName } : {}),
      ...(itemId ? { item_id: itemId } : {}),
      ...(listId ? { list_id: listId } : {}),
    });
    const outUrl = data?.url;
    if (!outUrl) throw new Error("no url in response");
    onLoadingChange && onLoadingChange(false);
    _navigateBlank(blank, _wrapWithTransit(outUrl));
    return data;
  } catch (err) {
    onLoadingChange && onLoadingChange(false);
    console.error("[goAmazon] outbound failed", err);
    // Fall back: build a tagged search URL client-side so the click
    // isn't a total dead end (we lose the server-side attribution log
    // but the tag is still on the outbound URL for Amazon).
    if (itemName) {
      const q = encodeURIComponent(itemName);
      const fallback = `https://www.amazon.com/s?k=${q}&tag=travelbio-20`;
      _navigateBlank(blank, _wrapWithTransit(fallback));
    } else if (url) {
      _navigateBlank(blank, _wrapWithTransit(url));
    } else {
      _closeBlank(blank);
    }
    if (String(err?.message || err) !== "no url in response") toast.error(FAILURE_TOAST);
    return { error: String(err?.message || err) };
  }
}
